// src/pages/public/Login.js
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "../../lib/firebase";
import useUserRole, { isSuperRole } from "../../hooks/useUserRole";

export default function Login() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useUserRole();

  // If a protected page sent them here, we’ll try to honor it.
  // But DO NOT default to /app (that route doesn’t exist in your current router).
  const from =
    (location.state && location.state.from && location.state.from.pathname) || null;

  const dashboardPath = () => {
    const isMgmt =
      role === "Admin" ||
      role === "Supervisor" ||
      role === "Super User" ||
      role === "Super Admin" ||
      isSuperRole(role);

    return isMgmt ? "/schedule" : "/my-schedule";
  };

  useEffect(() => {
    // If already signed in, push straight to the correct dashboard
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) {
       navigate("/dashboard", { replace: true });
 
      } else {
        setChecking(false);
      }
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, role]);

  const niceAuthError = (code, fallback) => {
    switch (code) {
      case "auth/invalid-credential":
      case "auth/wrong-password":
        return "Incorrect email or password.";
      case "auth/user-not-found":
        return "No account found for that email.";
      case "auth/too-many-requests":
        return "Too many attempts. Try again later or reset your password.";
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      default:
        return fallback || "Something went wrong. Please try again.";
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");
    if (busy) return;

    setBusy(true);
    try {
      // Read from the form in case the browser autofilled but React state didn’t update.
      const form = e.currentTarget.closest("form") || e.currentTarget;
      const fd = new FormData(form);
      const formEmail = (fd.get("email") || email || "")
        .toString()
        .trim()
        .toLowerCase();
      const formPw = (fd.get("password") || pw || "").toString();

      if (!formEmail || !formPw) {
        setErr("Enter your email and password.");
        return;
      }

      await signInWithEmailAndPassword(auth, formEmail, formPw);

      // Prefer returning to a protected page they tried to access,
      // but only if it exists in your router (not /app).
     navigate("/dashboard", { replace: true });
    } catch (e2) {
      setErr(niceAuthError(e2.code, e2.message));
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    setErr("");
    setMsg("");
    const addr = (email || "").trim().toLowerCase();
    if (!addr) {
      setErr("Enter your email first.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, addr);
      setMsg("Reset link sent. Check your inbox (and Spam).");
    } catch (e2) {
      setErr(niceAuthError(e2.code, e2.message));
    }
  };

  if (checking) {
    return (
      <div className="auth-container">
        <div>Loading…</div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <h1 className="auth-title">Sign in</h1>

      <form className="auth-form" onSubmit={handleSignIn} noValidate>
        <label className="auth-label" htmlFor="email">
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          className="auth-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onInput={(e) => setEmail(e.currentTarget.value)}
          placeholder="you@example.com"
          disabled={busy}
          required
        />

        <label className="auth-label" htmlFor="password">
          Password
        </label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          className="auth-input"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          onInput={(e) => setPw(e.currentTarget.value)}
          placeholder="••••••••"
          disabled={busy}
          required
        />

        {err && (
          <p className="auth-error" role="alert">
            {err}
          </p>
        )}
        {msg && <p className="auth-msg">{msg}</p>}

        <button type="submit" className="auth-submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>

        <button
          type="button"
          className="auth-link"
          onClick={handleReset}
          disabled={busy}
          style={{ marginTop: 10 }}
        >
          Forgot password?
        </button>

        <p className="auth-hint" style={{ marginTop: 8 }}>
          Forgot password? We’ll email you a reset link.
        </p>
      </form>
    </div>
  );
}
